import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, CalendarDays, Plus, X, Ban, Wrench, CheckCircle2 } from "lucide-react";
import { BIKE_STATUS, BIKE_HARD_BLOCKED } from "../../config";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WD = ["L", "M", "X", "J", "V", "S", "D"];
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };

// canal → color de la barra
const CH_COLOR = {
  online: "bg-topacio-green",
  partner: "bg-blue-500",
  tienda: "bg-topacio-red",
};
// canal → inicial para distinguirlo de un vistazo (W=Web · P=Partner · T=Tienda)
const CH_LABEL = { online: "W", partner: "P", tienda: "T" };

// reparte reservas solapadas en "carriles" para que no se pisen (estilo Riderly)
function toLanes(items) {
  const lanes = [];
  for (const it of items.slice().sort((a, b) => a.s - b.s)) {
    let placed = false;
    for (const lane of lanes) {
      if (lane[lane.length - 1].e < it.s) { lane.push(it); placed = true; break; }
    }
    if (!placed) lanes.push([it]);
  }
  return lanes;
}

// Coloca cada reserva en un carril. En modelos con varias unidades, las reservas
// asignadas a una matrícula van a su carril (unit_index); el resto al primer libre.
function placeItems(items, multi, stock) {
  if (!multi) {
    return toLanes(items).flatMap((lane, li) => lane.map((it) => ({ it, lane: li })));
  }
  const laneItems = Array.from({ length: stock }, () => []);
  const result = [];
  const isAssigned = (it) => it.unit_index != null && it.unit_index >= 0 && it.unit_index < stock;
  for (const it of items.filter(isAssigned)) {
    laneItems[it.unit_index].push(it);
    result.push({ it, lane: it.unit_index });
  }
  for (const it of items.filter((x) => !isAssigned(x)).sort((a, b) => a.s - b.s)) {
    let lane = stock - 1;
    for (let l = 0; l < stock; l++) {
      if (!laneItems[l].some((o) => !(o.e < it.s || o.s > it.e))) { lane = l; break; }
    }
    laneItems[lane].push(it);
    result.push({ it, lane });
  }
  return result;
}

// carriles bloqueados por avería/reparación (moto completa o unidad concreta) → se pintan como ocupados
function blockedLanes(bike, multi, stock) {
  const out = [];
  if (multi) {
    for (let i = 0; i < stock; i++) {
      const us = bike.units?.[i]?.status || "ok";
      if (BIKE_HARD_BLOCKED.includes(us)) out.push({ lane: i, status: us });
    }
  } else if (BIKE_HARD_BLOCKED.includes(bike.status || "ok")) {
    out.push({ lane: 0, status: bike.status });
  }
  return out;
}

export default function CalendarTab({ headers, canEdit }) {
  const [start, setStart] = useState(() => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; });
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [closed, setClosed] = useState([]);
  const [newDate, setNewDate] = useState("");
  const [customHours, setCustomHours] = useState({}); // {YYYY-MM-DD: "HH:MM"}
  const [chDate, setChDate] = useState("");
  const [chTime, setChTime] = useState("14:00");

  const load = () => {
    setLoading(true);
    axios.get(`${API}/admin/calendar?start=${iso(start)}&days=${days}`, { headers })
      .then(({ data }) => setData(data))
      .catch(() => toast.error("No se pudo cargar el calendario"))
      .finally(() => setLoading(false));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [start, days]);
  useEffect(() => {
    axios.get(`${API}/admin/closed-dates`, { headers })
      .then(({ data }) => setClosed(data.dates || [])).catch(() => {});
    axios.get(`${API}/admin/custom-hours`, { headers })
      .then(({ data }) => setCustomHours(data.hours || {})).catch(() => {});
  }, [headers]);

  const dayList = useMemo(() => (data?.day_list || []).map((s) => { const [y, m, d] = s.split("-").map(Number); return new Date(y, m - 1, d); }), [data]);
  const monthLabel = useMemo(() => {
    if (!dayList.length) return "";
    const a = dayList[0], b = dayList[dayList.length - 1];
    const full = (d) => d.toLocaleDateString("es-ES", { month: "long", year: "numeric" });
    if (a.getMonth() === b.getMonth() && a.getFullYear() === b.getFullYear()) return full(a);
    return `${a.toLocaleDateString("es-ES", { month: "short" })} – ${full(b)}`;
  }, [dayList]);
  const closedSet = useMemo(() => new Set(data?.closed_dates || []), [data]);
  const startISO = data?.start;

  // reservas indexadas por moto con posición (offset y span dentro de la ventana)
  // offset = diferencia de días natural (Date.UTC evita el salto del cambio de hora)
  const byBike = useMemo(() => {
    const map = {};
    if (!data) return map;
    const utc = (s) => { const [y, m, d] = String(s).split("-").map(Number); return Date.UTC(y, m - 1, d); };
    const win0 = utc(startISO);
    for (const bk of data.bookings) {
      const s0 = Math.max(0, Math.round((utc(bk.start_date) - win0) / 86400000));
      const e0 = Math.min(days - 1, Math.round((utc(bk.end_date) - win0) / 86400000));
      if (e0 < 0 || s0 > days - 1) continue;
      (map[bk.bike_slug] = map[bk.bike_slug] || []).push({ ...bk, s: s0, e: e0 });
    }
    return map;
  }, [data, days, startISO]);

  const saveClosed = (list) => {
    axios.put(`${API}/admin/closed-dates`, { dates: list }, { headers })
      .then(({ data }) => { setClosed(data.dates || []); toast.success("Festivos actualizados"); load(); })
      .catch(() => toast.error("No se pudo guardar"));
  };
  const addClosed = () => { if (newDate && !closed.includes(newDate)) { saveClosed([...closed, newDate]); setNewDate(""); } };
  const removeClosed = (d) => saveClosed(closed.filter((x) => x !== d));

  const saveHours = (map) => {
    axios.put(`${API}/admin/custom-hours`, { hours: map }, { headers })
      .then(({ data }) => { setCustomHours(data.hours || {}); toast.success("Horario especial guardado"); })
      .catch(() => toast.error("No se pudo guardar"));
  };
  const addHour = () => { if (chDate && chTime) { saveHours({ ...customHours, [chDate]: chTime }); setChDate(""); } };
  const removeHour = (d) => { const m = { ...customHours }; delete m[d]; saveHours(m); };

  const colW = 30; // px por día
  const labelW = 190;

  // --- Editable: mover barra · redimensionar bordes · clic para editar · clic en hueco para crear ---
  const [drag, setDrag] = useState(null); // {ref, mode, startX, realStart, realEnd}
  const [dragDelta, setDragDelta] = useState(0);
  const [createCell, setCreateCell] = useState(null); // {slug, name, dayISO}
  const [cName, setCName] = useState("");
  const [cDays, setCDays] = useState(1);
  const [saving, setSaving] = useState(false);
  const [editRef, setEditRef] = useState(null); // reserva abierta para edición rápida

  const shiftISO = (isoStr, d) => { const [y, m, dd] = isoStr.split("-").map(Number); const x = new Date(y, m - 1, dd); x.setDate(x.getDate() + d); return iso(x); };

  useEffect(() => {
    if (!drag) return;
    const onMove = (e) => setDragDelta(Math.round((e.clientX - drag.startX) / colW));
    const onUp = (e) => {
      const delta = Math.round((e.clientX - drag.startX) / colW);
      const { ref, mode, realStart, realEnd } = drag;
      setDrag(null); setDragDelta(0);
      // clic sin desplazamiento sobre el cuerpo de la barra → abrir editor
      if (mode === "move" && delta === 0) { setEditRef(ref); return; }
      if (delta === 0) return;
      let ns = realStart, ne = realEnd;
      if (mode === "move") { ns = shiftISO(realStart, delta); ne = shiftISO(realEnd, delta); }
      else if (mode === "resize-start") { ns = shiftISO(realStart, delta); if (ns > realEnd) ns = realEnd; }
      else if (mode === "resize-end") { ne = shiftISO(realEnd, delta); if (ne < realStart) ne = realStart; }
      if (ns === realStart && ne === realEnd) return;
      const verb = mode === "move" ? "movida" : "redimensionada";
      axios.patch(`${API}/admin/bookings/${ref}/dates`, { start_date: ns, end_date: ne }, { headers })
        .then(() => { toast.success(`Reserva ${verb}`); load(); })
        .catch((err) => toast.error(err?.response?.status === 409 ? "Esas fechas no tienen stock" : "No se pudo actualizar"));
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => { window.removeEventListener("mousemove", onMove); window.removeEventListener("mouseup", onUp); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drag]);

  const startDrag = (e, it, mode = "move") => {
    if (!canEdit) return;
    e.preventDefault();
    e.stopPropagation();
    setDrag({ ref: it.ref, mode, startX: e.clientX, realStart: it.start_date, realEnd: it.end_date });
  };

  const openCreate = (slug, name, dayISO) => {
    if (!canEdit || closedSet.has(dayISO)) return;
    setCreateCell({ slug, name, dayISO }); setCName(""); setCDays(1);
  };
  const submitCreate = () => {
    if (!createCell || !cName.trim()) { toast.error("Pon un nombre de cliente"); return; }
    setSaving(true);
    axios.post(`${API}/admin/bookings/manual`, {
      bike_slug: createCell.slug, name: cName.trim(),
      start_date: createCell.dayISO, end_date: shiftISO(createCell.dayISO, cDays), total: 0,
    }, { headers })
      .then(() => { toast.success("Reserva creada"); setCreateCell(null); load(); })
      .catch((err) => toast.error(err?.response?.status === 409 ? "Sin stock esas fechas" : "No se pudo crear"))
      .finally(() => setSaving(false));
  };

  return (
    <div data-testid="calendar-tab">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h2 className="font-display text-xl font-bold text-topacio-ink flex items-center gap-2">
          <CalendarDays className="w-5 h-5" /> Calendario de flota
          {monthLabel && <span data-testid="cal-month-label" className="text-sm font-extrabold text-topacio-red capitalize">· {monthLabel}</span>}
        </h2>
        <div className="flex items-center gap-2">
          <button data-testid="cal-prev" onClick={() => setStart(addDays(start, -days))} className="btn-secondary !px-3 !py-2"><ChevronLeft className="w-4 h-4" /></button>
          <button data-testid="cal-today" onClick={() => { const d = new Date(); d.setHours(0, 0, 0, 0); setStart(d); }} className="btn-secondary !px-3 !py-2 !text-xs">Hoy</button>
          <button data-testid="cal-next" onClick={() => setStart(addDays(start, days))} className="btn-secondary !px-3 !py-2"><ChevronRight className="w-4 h-4" /></button>
          <input type="date" data-testid="cal-jump-date" value={iso(start)}
            onChange={(e) => { if (!e.target.value) return; const [y, m, d] = e.target.value.split("-").map(Number); const nd = new Date(y, m - 1, d); nd.setHours(0, 0, 0, 0); setStart(nd); }}
            className="input-brutal !py-2 !w-auto !text-xs" title="Ir a una fecha concreta" />
          <select data-testid="cal-days" value={days} onChange={(e) => setDays(Number(e.target.value))} className="input-brutal !py-2 !w-auto !text-xs">
            <option value={14}>14 días</option>
            <option value={30}>30 días</option>
            <option value={45}>45 días</option>
            <option value={60}>60 días</option>
          </select>
        </div>
      </div>

      {/* leyenda */}
      <div className="flex flex-wrap items-center gap-4 mb-4 text-[11px] font-bold text-topacio-faded">
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-topacio-green inline-block" /> Web (W)</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-blue-500 inline-block" /> Partner (P)</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-topacio-red inline-block" /> Tienda (T)</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-zinc-300 inline-block" /> Cerrado / festivo</span>
        <span className="flex items-center gap-1.5"><span className="inline-flex items-center justify-center w-3.5 h-3.5 rounded-full bg-topacio-red text-white text-[8px] font-extrabold leading-none">€</span> Anticipo 50% pendiente</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-violet-600 inline-block" /> Entregada</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm inline-block" style={{ backgroundImage: "repeating-linear-gradient(45deg, #E63946 0, #E63946 3px, #111 3px, #111 6px)" }} /> En reparación</span>
      </div>

      <div className="card-brutal p-0 overflow-x-auto">
        {canEdit && (
          <div className="px-4 py-2 text-[11px] font-bold text-topacio-faded bg-topacio-sand/60 border-b-2 border-topacio-ink/10">
            Arrastra una barra para mover · arrastra los bordes para alargar/acortar · haz clic en una barra para editar · clic en un hueco libre para crear una reserva de tienda
          </div>
        )}
        <div className="min-w-max">
          {/* cabecera de días */}
          <div className="flex sticky top-0 z-20 bg-white border-b-2 border-topacio-ink">
            <div style={{ width: labelW }} className="shrink-0 px-3 py-2 font-extrabold text-[11px] uppercase tracking-wider text-topacio-faded border-r-2 border-topacio-ink">Modelo</div>
            {dayList.map((d) => {
              const key = iso(d);
              const wd = (d.getDay() + 6) % 7; // 0=Lunes
              const isClosed = closedSet.has(key);
              const isToday = key === iso(new Date());
              const isSunday = d.getDay() === 0;
              return (
                <div key={key} style={{ width: colW }} className={`shrink-0 text-center py-1.5 border-r border-zinc-200 ${isClosed ? "bg-zinc-100" : isSunday ? "bg-topacio-red/10" : ""} ${isToday ? "bg-brand/40" : ""}`}>
                  <div className={`text-[9px] font-bold ${wd >= 5 ? "text-topacio-red" : "text-topacio-faded"}`}>{WD[wd]}</div>
                  <div className="text-[11px] font-extrabold text-topacio-ink">{d.getDate()}</div>
                </div>
              );
            })}
          </div>

          {/* filas de motos */}
          {loading && <div className="px-4 py-6 text-sm font-bold text-topacio-faded">Cargando…</div>}
          {!loading && (data?.bikes || []).map((bike) => {
            const items = byBike[bike.slug] || [];
            const multi = (bike.stock || 1) > 1;
            const placed = placeItems(items, multi, bike.stock || 1);
            const laneCount = Math.max(1, multi ? (bike.stock || 1) : 0, ...placed.map((p) => p.lane + 1));
            const blocks = blockedLanes(bike, multi, bike.stock || 1);
            const rowH = laneCount * 26 + 8;
            const unitLabel = (i) => {
              const u = bike.units?.[i] || {};
              const parts = [u.internal_name, u.plate].filter(Boolean);
              return parts.length ? parts.join(" · ") : `Unidad ${i + 1}`;
            };
            return (
              <div key={bike.slug} data-testid={`cal-row-${bike.slug}`} className="flex border-b border-zinc-200 hover:bg-topacio-sand/40">
                <div style={{ width: labelW, minHeight: rowH }} className="shrink-0 px-3 py-2 border-r-2 border-topacio-ink">
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-[12px] text-topacio-ink leading-tight">{bike.name}</span>
                    {multi && <span className="badge-brutal bg-topacio-ink text-brand !text-[8px] !px-1.5">{bike.stock}</span>}
                  </div>
                  {multi && (
                    <div className="mt-1 space-y-0.5">
                      {Array.from({ length: bike.stock }).map((_, i) => {
                        const us = bike.units?.[i]?.status || "ok";
                        return (
                          <div key={i} data-testid={`cal-unit-${bike.slug}-${i}`} className="flex items-center gap-1 h-[22px] text-[10px] font-bold text-topacio-faded leading-none truncate">
                            <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-topacio-ink/10 text-[8px] shrink-0">{i + 1}</span>
                            <span className="truncate">{unitLabel(i)}</span>
                            {us !== "ok" && (
                              <span data-testid={`cal-unit-status-${bike.slug}-${i}`} title={(BIKE_STATUS[us] || {}).label || us}
                                className={`shrink-0 w-2 h-2 rounded-full border border-black/20 ${(BIKE_STATUS[us] || BIKE_STATUS.ok).cls}`} />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
                <div className="relative" style={{ width: colW * days, minHeight: rowH }}>
                  {/* separadores de carril para distinguir las unidades del mismo modelo */}
                  {multi && Array.from({ length: bike.stock }).map((_, i) => (
                    <div key={`sep-${i}`} className="absolute left-0 right-0 border-t border-dashed border-zinc-200" style={{ top: i * 26 }} />
                  ))}
                  {/* rejilla de fondo (días cerrados / clic para crear) */}
                  {dayList.map((d, i) => {
                    const key = iso(d);
                    const isSunday = d.getDay() === 0;
                    return <div key={key} onClick={() => openCreate(bike.slug, bike.name, key)}
                      style={{ left: i * colW, width: colW }}
                      className={`absolute top-0 bottom-0 border-r border-zinc-100 ${closedSet.has(key) ? "bg-zinc-100" : isSunday ? "bg-topacio-red/10" : canEdit ? "hover:bg-brand/20 cursor-pointer" : ""}`}>
                      <span className="pointer-events-none select-none absolute inset-x-0 top-0.5 text-center text-[10px] font-extrabold text-zinc-300">{d.getDate()}</span>
                    </div>;
                  })}
                  {/* carriles bloqueados por avería/reparación: ocupan todo el ancho, no reservables */}
                  {blocks.map(({ lane, status }) => (
                    <div key={`block-${lane}`} data-testid={`cal-repair-block-${bike.slug}-${lane}`}
                      title={`${(BIKE_STATUS[status] || {}).label || status} · no disponible`}
                      style={{ left: 2, width: colW * days - 4, top: lane * 26 + 5, height: 22,
                        backgroundImage: "repeating-linear-gradient(45deg, rgba(230,57,70,0.85) 0, rgba(230,57,70,0.85) 7px, rgba(17,17,17,0.85) 7px, rgba(17,17,17,0.85) 14px)" }}
                      className="absolute rounded-md flex items-center px-2 text-white text-[10px] font-extrabold uppercase tracking-wider overflow-hidden whitespace-nowrap shadow-sm border border-black/20 z-[1]">
                      <Wrench className="w-3 h-3 mr-1 shrink-0" /> {(BIKE_STATUS[status] || {}).label || status}
                    </div>
                  ))}
                  {/* barras de reserva (arrastrar cuerpo = mover · bordes = redimensionar · clic = editar) */}
                  {placed.map(({ it, lane: li }) => {
                    const isDragging = drag?.ref === it.ref;
                    const delivered = it.delivery_status === "entregada";
                    const u = bike.units?.[it.unit_index];
                    const unitTip = bike.stock > 1
                      ? (it.unit_index != null && u ? ` · ${u.internal_name || u.plate || ("Unidad " + (it.unit_index + 1))}` : " · NINGUNA UNIDAD ASIGNADA")
                      : "";
                    let dS = it.s, dE = it.e;
                    if (isDragging) {
                      if (drag.mode === "move") { dS = it.s + dragDelta; dE = it.e + dragDelta; }
                      else if (drag.mode === "resize-start") { dS = Math.min(it.s + dragDelta, it.e); }
                      else if (drag.mode === "resize-end") { dE = Math.max(it.e + dragDelta, it.s); }
                    }
                    return (
                      <div key={it.ref}
                        data-testid={`cal-booking-${it.ref}`}
                        onMouseDown={(e) => startDrag(e, it, "move")}
                        title={`${it.name || ""}${unitTip} · ${it.start_date} ${it.pickup_time || ""}→${it.end_date} ${it.return_time || ""} · ${it.channel || "web"}${delivered ? " · ENTREGADA" : ""}${(it.channel || "online") === "online" && it.status === "pending" ? ` · ANTICIPO 50% PENDIENTE (${it.advance_amount != null ? it.advance_amount : Math.round((it.total || 0) * 0.5)}€)` : ""}${canEdit ? " · arrastra para mover, bordes para redimensionar, clic para editar" : ""}`}
                        style={{ left: dS * colW + 2, width: (dE - dS + 1) * colW - 4, top: li * 26 + 5, opacity: isDragging ? 0.7 : 1 }}
                        className={`absolute h-[22px] rounded-md ${delivered ? "bg-violet-600 ring-1 ring-violet-300" : (CH_COLOR[it.channel] || "bg-topacio-green")} text-white text-[10px] font-bold flex items-center px-1.5 overflow-hidden whitespace-nowrap shadow-sm border border-black/10 z-[2] ${canEdit ? "cursor-grab active:cursor-grabbing" : ""}`}>
                        {canEdit && (
                          <span data-testid={`cal-resize-start-${it.ref}`} onMouseDown={(e) => startDrag(e, it, "resize-start")}
                            className="absolute left-0 top-0 bottom-0 w-1.5 cursor-ew-resize bg-black/25 hover:bg-black/50 rounded-l-md" />
                        )}
                        <span className="pointer-events-none truncate px-1 flex items-center gap-1">
                          {delivered && (
                            <span data-testid={`cal-booking-delivered-${it.ref}`} title="Moto entregada al cliente">
                              <CheckCircle2 className="w-3.5 h-3.5 shrink-0 text-white" />
                            </span>
                          )}
                          {(it.channel || "online") === "online" && it.status === "pending" && (
                            <span data-testid={`cal-booking-unpaid-${it.ref}`} title="Anticipo 50% pendiente de pago"
                              className="shrink-0 inline-flex items-center justify-center w-3.5 h-3.5 rounded-full bg-topacio-red text-white text-[8px] font-extrabold leading-none ring-1 ring-white/70">€</span>
                          )}
                          <span data-testid={`cal-booking-channel-${it.ref}`} className="shrink-0 inline-flex items-center justify-center w-3.5 h-3.5 rounded-full bg-white/30 text-[8px] font-extrabold leading-none" title={it.channel || "online"}>{CH_LABEL[it.channel] || "W"}</span>
                          <span className="truncate">{(it.name || "Reserva").slice(0, Math.max(3, (dE - dS + 1) * 4))}</span>
                          {(it.pickup_time || it.return_time) && (dE - dS) >= 1 && (
                            <span data-testid={`cal-booking-hours-${it.ref}`} className="shrink-0 font-semibold text-white/80 tabular-nums">{it.pickup_time || "--"}→{it.return_time || "--"}</span>
                          )}
                        </span>
                        {canEdit && (
                          <span data-testid={`cal-resize-end-${it.ref}`} onMouseDown={(e) => startDrag(e, it, "resize-end")}
                            className="absolute right-0 top-0 bottom-0 w-1.5 cursor-ew-resize bg-black/25 hover:bg-black/50 rounded-r-md" />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* editor de festivos / días cerrados */}
      <div className="card-brutal p-6 mt-8">
        <h3 className="font-display font-bold text-topacio-ink flex items-center gap-2 mb-2"><Ban className="w-4 h-4 text-topacio-red" /> Festivos y días cerrados</h3>
        <p className="text-[12px] text-topacio-faded font-semibold mb-4">Bloquea fechas concretas (puentes, festivos locales, vacaciones). Los clientes no podrán reservar recogida ni devolución esos días. Los domingos y 25 dic / 1 y 6 ene ya están cerrados por defecto.</p>
        {canEdit && (
          <div className="flex items-center gap-2 mb-4">
            <input data-testid="closed-date-input" type="date" value={newDate} onChange={(e) => setNewDate(e.target.value)} className="input-brutal !w-auto !py-2" />
            <button data-testid="closed-date-add" onClick={addClosed} disabled={!newDate} className="btn-primary !px-4 !py-2 !text-xs flex items-center gap-1.5"><Plus className="w-4 h-4" /> Añadir</button>
          </div>
        )}
        <div className="flex flex-wrap gap-2">
          {closed.length === 0 && <span className="text-[12px] text-topacio-faded font-semibold">Sin días cerrados extra.</span>}
          {closed.map((d) => (
            <span key={d} data-testid={`closed-chip-${d}`} className="inline-flex items-center gap-2 rounded-full border-2 border-topacio-ink bg-white px-3 py-1 text-[12px] font-bold text-topacio-ink">
              {d}
              {canEdit && <button data-testid={`closed-remove-${d}`} onClick={() => removeClosed(d)} className="text-topacio-red hover:scale-110 transition-transform"><X className="w-3.5 h-3.5" /></button>}
            </span>
          ))}
        </div>
      </div>

      {/* editor de horarios especiales por fecha (cierre a una hora concreta) */}
      <div className="card-brutal p-6 mt-6" data-testid="custom-hours-card">
        <h3 className="font-display font-bold text-topacio-ink flex items-center gap-2 mb-2"><CalendarDays className="w-4 h-4 text-topacio-red" /> Horarios especiales por fecha</h3>
        <p className="text-[12px] text-topacio-faded font-semibold mb-4">Adelanta la hora de cierre un día concreto (ej. 12-oct cerramos a las 14:00). Ese día los clientes solo podrán elegir recogida/devolución hasta esa hora. El resto de días mantienen el horario normal (L-V hasta 17:30, sábados hasta 13:30).</p>
        {canEdit && (
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <input data-testid="custom-hour-date" type="date" value={chDate} onChange={(e) => setChDate(e.target.value)} className="input-brutal !w-auto !py-2" />
            <span className="text-[12px] font-bold text-topacio-faded">cierra a las</span>
            <input data-testid="custom-hour-time" type="time" value={chTime} onChange={(e) => setChTime(e.target.value)} className="input-brutal !w-auto !py-2" />
            <button data-testid="custom-hour-add" onClick={addHour} disabled={!chDate || !chTime} className="btn-primary !px-4 !py-2 !text-xs flex items-center gap-1.5"><Plus className="w-4 h-4" /> Añadir</button>
          </div>
        )}
        <div className="flex flex-wrap gap-2">
          {Object.keys(customHours).length === 0 && <span className="text-[12px] text-topacio-faded font-semibold">Sin horarios especiales.</span>}
          {Object.entries(customHours).sort(([a], [b]) => a.localeCompare(b)).map(([d, t]) => (
            <span key={d} data-testid={`custom-hour-chip-${d}`} className="inline-flex items-center gap-2 rounded-full border-2 border-topacio-ink bg-white px-3 py-1 text-[12px] font-bold text-topacio-ink">
              {d} · hasta {t}
              {canEdit && <button data-testid={`custom-hour-remove-${d}`} onClick={() => removeHour(d)} className="text-topacio-red hover:scale-110 transition-transform"><X className="w-3.5 h-3.5" /></button>}
            </span>
          ))}
        </div>
      </div>

      {createCell && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="cal-create-modal" onClick={() => setCreateCell(null)}>
          <div className="card-brutal bg-white p-6 w-full max-w-sm" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display font-bold text-topacio-ink mb-1">Nueva reserva en tienda</h3>
            <p className="text-[12px] text-topacio-faded font-semibold mb-4">{createCell.name} · desde {createCell.dayISO}</p>
            <label className="text-xs font-extrabold uppercase tracking-wider text-topacio-faded">Cliente</label>
            <input data-testid="cal-create-name" value={cName} onChange={(e) => setCName(e.target.value)} placeholder="Nombre del cliente" className="input-brutal mt-1 mb-3" autoFocus />
            <label className="text-xs font-extrabold uppercase tracking-wider text-topacio-faded">Días</label>
            <input data-testid="cal-create-days" type="number" min={1} value={cDays} onChange={(e) => setCDays(Math.max(parseInt(e.target.value) || 1, 1))} className="input-brutal mt-1 mb-4" />
            <div className="flex gap-2">
              <button data-testid="cal-create-submit" onClick={submitCreate} disabled={saving} className="btn-primary flex-1 !py-2.5">{saving ? "Creando…" : "Crear reserva"}</button>
              <button onClick={() => setCreateCell(null)} className="btn-secondary !py-2.5">Cancelar</button>
            </div>
            <p className="text-[11px] text-topacio-faded mt-3">Luego podrás completar importe, extras y documentos desde la pestaña Reservas.</p>
          </div>
        </div>
      )}

      {editRef && (
        <EditBookingModal refId={editRef} headers={headers} canEdit={canEdit} bikes={data?.bikes || []}
          onClose={() => setEditRef(null)}
          onSaved={() => { setEditRef(null); load(); }} />
      )}
    </div>
  );
}

const EDIT_STATUSES = [
  ["pending", "Pendiente"], ["deposit_paid", "Depósito pagado"], ["confirmed", "Confirmada"],
  ["completed", "Completada"], ["cancelled", "Cancelada"],
];

function EditBookingModal({ refId, headers, canEdit, bikes, onClose, onSaved }) {
  const [bk, setBk] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [applyDirectly, setApplyDirectly] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/bookings/${refId}`, { headers })
      .then(({ data }) => {
        const b = data.booking;
        setBk(b);
        setForm({
          name: b.name || "", phone: b.phone || "", email: b.email || "",
          total: b.total != null ? b.total : 0,
          deposit_amount: b.deposit_amount != null ? b.deposit_amount : 0,
          status: b.status || "pending", notes: b.message || "",
          start_date: b.start_date || "", end_date: b.end_date || "",
          pickup_time: b.pickup_time || "10:00", return_time: b.return_time || "19:00",
          unit_index: b.unit_index != null ? b.unit_index : "",
          bike_slug: b.bike_slug || "",
          contract: b.contract || {},
        });
      })
      .catch(() => { toast.error("No se pudo cargar la reserva"); onClose(); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refId]);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const updCt = (k, v) => setForm((f) => ({ ...f, contract: { ...(f.contract || {}), [k]: v } }));
  const modelBike = form ? (bikes || []).find((x) => x.slug === form.bike_slug) : null;
  const units = modelBike?.units || [];
  const stock = modelBike?.stock || 1;
  const modelChanged = bk && form && form.bike_slug !== bk.bike_slug;
  const hasEmail = !!(bk && bk.email);
  // Con email → por defecto se envía propuesta al cliente (debe aceptarla). Sin email → se aplica ya.
  const willPropose = modelChanged && hasEmail && !applyDirectly;
  // al cambiar de modelo, las unidades son otras → limpiar la asignación
  const changeModel = (slug) => setForm((f) => ({ ...f, bike_slug: slug, unit_index: "" }));

  const errMsg = (err) => {
    const d = err?.response?.data?.detail;
    return {
      dates_unavailable: "Esas fechas no tienen stock para este modelo",
      bike_unavailable: "El nuevo modelo no tiene unidades libres en esas fechas",
      bike_not_found: "Ese modelo no existe o está inactivo",
      unit_taken: "Esa matrícula ya está ocupada en esas fechas",
      invalid_unit: "Unidad no válida para este modelo",
      no_customer_email: "La reserva no tiene email del cliente: márcalo como aplicar directamente",
      same_bike: "Es el mismo modelo",
    }[d] || d || "No se pudo guardar";
  };

  const save = () => {
    if (!form.name.trim()) { setError("El nombre del cliente es obligatorio"); return; }
    if (form.end_date < form.start_date) { setError("La devolución no puede ser anterior a la recogida"); return; }
    setSaving(true); setError("");
    const payload = {
      name: form.name, phone: form.phone, email: form.email,
      total: Number(form.total) || 0, deposit_amount: Number(form.deposit_amount) || 0,
      status: form.status, notes: form.notes,
      start_date: form.start_date, end_date: form.end_date,
      pickup_time: form.pickup_time, return_time: form.return_time,
      contract: form.contract || {},
    };
    // Si se aplica directamente (o no hay email), el cambio de modelo va en el PATCH de detalles.
    if (modelChanged && !willPropose) payload.bike_slug = form.bike_slug;
    if (!modelChanged && stock > 1 && form.unit_index !== "") payload.unit_index = Number(form.unit_index);
    axios.patch(`${API}/admin/bookings/${refId}/details`, payload, { headers })
      .then(async () => {
        if (willPropose) {
          const { data } = await axios.post(`${API}/admin/bookings/${refId}/propose-model-change`, { bike_slug: form.bike_slug }, { headers });
          toast.success(data.email_sent ? `Propuesta enviada a ${bk.email}. La moto no cambia hasta que el cliente la acepte.` : "Propuesta registrada (el email se enviará en producción).");
        } else {
          toast.success(modelChanged ? "Modelo cambiado y reserva actualizada" : "Reserva actualizada");
        }
        onSaved();
      })
      .catch((err) => { setError(errMsg(err)); setSaving(false); });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="cal-edit-modal" onClick={onClose}>
      <div className="card-brutal bg-white p-6 w-full max-w-md max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        {!form ? (
          <p className="text-sm font-bold text-topacio-faded py-6 text-center">Cargando reserva…</p>
        ) : (
          <>
            <div className="flex items-center justify-between mb-1">
              <h3 className="font-display font-bold text-topacio-ink">Editar reserva {bk?.ref}</h3>
              <button data-testid="cal-edit-close" onClick={onClose} className="text-topacio-faded hover:text-topacio-ink"><X className="w-5 h-5" /></button>
            </div>
            <p className="text-[12px] text-topacio-faded font-semibold mb-4">{bk?.bike_name} · {bk?.start_date} → {bk?.end_date}</p>

            <label className="text-xs font-extrabold uppercase tracking-wider text-topacio-faded">Modelo de moto</label>
            <select data-testid="cal-edit-bike" value={form.bike_slug} onChange={(e) => changeModel(e.target.value)} disabled={!canEdit} className="input-brutal mt-1 mb-1">
              {(bikes || []).map((x) => <option key={x.slug} value={x.slug}>{x.name}</option>)}
            </select>
            {modelChanged && (
              <div data-testid="cal-edit-model-note" className="mb-3 mt-1 rounded-xl border-2 border-topacio-ink/15 bg-topacio-sand/60 p-3">
                <p className="text-[11px] font-bold text-topacio-red">Vas a cambiar el modelo a {modelBike?.name || form.bike_slug}. Revisa el importe y la fianza (no se recalculan solos).</p>
                {hasEmail ? (
                  <label className="flex items-start gap-2 mt-2 cursor-pointer">
                    <input data-testid="cal-edit-apply-directly" type="checkbox" checked={applyDirectly} onChange={(e) => setApplyDirectly(e.target.checked)} className="mt-0.5" disabled={!canEdit} />
                    <span className="text-[11px] text-topacio-ink font-semibold leading-snug">
                      Aplicar directamente sin pedir permiso al cliente.
                      <span className="block text-topacio-faded font-normal">{applyDirectly ? "El cambio se guardará al instante." : `Por defecto: se enviará un email a ${bk?.email} para que el cliente acepte el cambio. La moto no cambia hasta entonces.`}</span>
                    </span>
                  </label>
                ) : (
                  <p className="text-[11px] text-topacio-faded font-semibold mt-1">Esta reserva no tiene email del cliente: el cambio se aplicará directamente.</p>
                )}
              </div>
            )}
            {!modelChanged && <div className="mb-3" />}

            <label className="text-xs font-extrabold uppercase tracking-wider text-topacio-faded">Cliente</label>
            <input data-testid="cal-edit-name" value={form.name} onChange={(e) => upd("name", e.target.value)} disabled={!canEdit} className="input-brutal mt-1 mb-3" />

            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <label className="text-xs font-extrabold uppercase tracking-wider text-topacio-faded">Teléfono</label>
                <input data-testid="cal-edit-phone" value={form.phone} onChange={(e) => upd("phone", e.target.value)} disabled={!canEdit} className="input-brutal mt-1" />
              </div>
              <div>
                <label className="text-xs font-extrabold uppercase tracking-wider text-topacio-faded">Email</label>
                <input data-testid="cal-edit-email" value={form.email} onChange={(e) => upd("email", e.target.value)} disabled={!canEdit} className="input-brutal mt-1" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <label className="text-xs font-extrabold uppercase tracking-wider text-topacio-faded">Importe total (€)</label>
                <input data-testid="cal-edit-total" type="number" min={0} step="0.01" value={form.total} onChange={(e) => upd("total", e.target.value)} disabled={!canEdit} className="input-brutal mt-1" />
              </div>
              <div>
                <label className="text-xs font-extrabold uppercase tracking-wider text-topacio-faded">Fianza (€)</label>
                <input data-testid="cal-edit-deposit" type="number" min={0} step="0.01" value={form.deposit_amount} onChange={(e) => upd("deposit_amount", e.target.value)} disabled={!canEdit} className="input-brutal mt-1" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <label className="text-xs font-extrabold uppercase tracking-wider text-topacio-faded">Recogida</label>
                <input data-testid="cal-edit-start" type="date" value={form.start_date} onChange={(e) => upd("start_date", e.target.value)} disabled={!canEdit} className="input-brutal mt-1" />
              </div>
              <div>
                <label className="text-xs font-extrabold uppercase tracking-wider text-topacio-faded">Devolución</label>
                <input data-testid="cal-edit-end" type="date" value={form.end_date} onChange={(e) => upd("end_date", e.target.value)} disabled={!canEdit} className="input-brutal mt-1" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <label className="text-xs font-extrabold uppercase tracking-wider text-topacio-faded">Hora recogida</label>
                <input data-testid="cal-edit-pickup-time" type="time" value={form.pickup_time} onChange={(e) => upd("pickup_time", e.target.value)} disabled={!canEdit} className="input-brutal mt-1" />
              </div>
              <div>
                <label className="text-xs font-extrabold uppercase tracking-wider text-topacio-faded">Hora devolución</label>
                <input data-testid="cal-edit-return-time" type="time" value={form.return_time} onChange={(e) => upd("return_time", e.target.value)} disabled={!canEdit} className="input-brutal mt-1" />
              </div>
            </div>

            <label className="text-xs font-extrabold uppercase tracking-wider text-topacio-faded">Estado</label>
            <select data-testid="cal-edit-status" value={form.status} onChange={(e) => upd("status", e.target.value)} disabled={!canEdit} className="input-brutal mt-1 mb-3">
              {EDIT_STATUSES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>

            {stock > 1 && (
              <div className="mb-3">
                <label className="text-xs font-extrabold uppercase tracking-wider text-topacio-faded">Unidad / matrícula asignada</label>
                <select data-testid="cal-edit-unit" value={form.unit_index} onChange={(e) => upd("unit_index", e.target.value)} disabled={!canEdit} className="input-brutal mt-1">
                  <option value="">Sin asignar</option>
                  {Array.from({ length: stock }).map((_, i) => (
                    <option key={i} value={i}>{units[i]?.internal_name || units[i]?.plate || `Unidad ${i + 1}`}{units[i]?.plate ? ` · ${units[i].plate}` : ""}</option>
                  ))}
                </select>
              </div>
            )}

            <label className="text-xs font-extrabold uppercase tracking-wider text-topacio-faded">Notas</label>
            <textarea data-testid="cal-edit-notes" value={form.notes} onChange={(e) => upd("notes", e.target.value)} disabled={!canEdit} rows={2} className="input-brutal mt-1 mb-4" />

            <details className="mb-4 rounded-xl border-2 border-topacio-ink/15 bg-topacio-sand/40 p-3" data-testid="cal-edit-contract">
              <summary className="cursor-pointer text-xs font-extrabold uppercase tracking-wider text-topacio-ink">Datos del contrato / SES Hospedajes</summary>
              <p className="text-[11px] text-topacio-faded font-semibold mt-2 mb-3">Se rellenan solos si el cliente los envía en el pre-checkin. Puedes completarlos o corregirlos aquí; salen en el contrato PDF.</p>
              <div className="grid grid-cols-2 gap-2">
                {[["surname1", "1º apellido"], ["surname2", "2º apellido"], ["birth_date", "Fecha nacimiento"], ["doc_number", "Nº documento"],
                  ["license_number", "Nº carnet"], ["license_type", "Tipo carnet (A1/B)"], ["license_country", "País del carnet"], ["license_issue", "Expedición carnet"],
                  ["license_expiry", "Caducidad carnet"], ["address", "Dirección"], ["city", "Municipio"], ["province", "Provincia"],
                  ["country", "País"], ["postal_code", "Código postal"]].map(([k, label]) => (
                  <input key={k} data-testid={`cal-edit-ct-${k}`} value={(form.contract || {})[k] || ""} disabled={!canEdit}
                    onChange={(e) => updCt(k, e.target.value)} placeholder={label} className="input-brutal !py-2 !text-sm" />
                ))}
              </div>
            </details>

            {error && <p data-testid="cal-edit-error" className="text-[12px] font-bold text-topacio-red mb-3">{error}</p>}

            {canEdit ? (
              <div className="flex gap-2">
                <button data-testid="cal-edit-save" onClick={save} disabled={saving} className="btn-primary flex-1 !py-2.5">{saving ? "Guardando…" : (willPropose ? "Enviar propuesta al cliente" : "Guardar cambios")}</button>
                <button onClick={onClose} className="btn-secondary !py-2.5">Cancelar</button>
              </div>
            ) : (
              <p className="text-[12px] text-topacio-faded font-semibold">Solo lectura.</p>
            )}
          </>
        )}
      </div>
    </div>
  );
}
